import { useState, useEffect } from 'react'
import { Card, Table, Button, Badge, Modal, Form, Row, Col, Spinner, Alert, InputGroup } from 'react-bootstrap'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import api from '../../services/api'
import { formatDateOnlyEsMX } from '../../utils/apiDateFormat'
import { formatCurrency } from '../../utils/formatters'

const MatchingInterface = ({ reconciliation, movimientos, onClose, onRefresh }) => {
  const queryClient = useQueryClient()
  const [matches, setMatches] = useState({ sugeridos: [], sin_match: [] })
  const [loadingMatches, setLoadingMatches] = useState(false)
  const [rangoDias, setRangoDias] = useState(3)
  const [selectedMovimiento, setSelectedMovimiento] = useState(null)
  const [showManualMatch, setShowManualMatch] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')

  // Obtener matches sugeridos
  const fetchMatches = async () => {
    if (!reconciliation?.id) return
    setLoadingMatches(true)
    try {
      const response = await api.get(
        `/financial/bank-reconciliations/${reconciliation.id}/matches/`,
        { params: { rango_dias: rangoDias } }
      )
      setMatches(response.data)
    } catch (error) {
      toast.error('Error al obtener matches: ' + (error.response?.data?.error || error.message))
    } finally {
      setLoadingMatches(false)
    }
  }

  useEffect(() => {
    if (reconciliation?.id) {
      fetchMatches()
    }
    // fetchMatches se redefine en cada render; incluirla provoca bucle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reconciliation?.id, rangoDias])

  // Mutación para confirmar match
  const confirmMatchMutation = useMutation(
    async ({ movimiento_id, transaccion_id, pago_id }) => {
      const response = await api.post(
        `/financial/bank-reconciliations/${reconciliation.id}/confirmar_match/`,
        { movimiento_id, transaccion_id, pago_id }
      )
      return response.data
    },
    {
      onSuccess: () => {
        toast.success('Match confirmado exitosamente')
        queryClient.invalidateQueries(['reconciliation-movements', reconciliation.id])
        fetchMatches()
        if (onRefresh) onRefresh()
      },
      onError: (error) => {
        const errorMsg = error.response?.data?.error || 'Error al confirmar match'
        toast.error(errorMsg)
      }
    }
  )

  // Mutación para descartar movimiento
  const descartarMutation = useMutation(
    async (movimiento_id) => {
      const response = await api.post(
        `/financial/bank-reconciliations/${reconciliation.id}/descartar_movimiento/`,
        { movimiento_id }
      )
      return response.data
    },
    {
      onSuccess: () => {
        toast.success('Movimiento descartado')
        queryClient.invalidateQueries(['reconciliation-movements', reconciliation.id])
        fetchMatches()
        if (onRefresh) onRefresh()
      },
      onError: (error) => {
        const errorMsg = error.response?.data?.error || 'Error al descartar movimiento'
        toast.error(errorMsg)
      }
    }
  )

  // Obtener pagos no conciliados para búsqueda manual
  const { data: pagosNoConciliados } = useQuery(
    ['unreconciled-payments', reconciliation?.bank_account],
    async () => {
      if (!reconciliation?.bank_account) return []
      try {
        const response = await api.get('/financial/payments/', {
          params: {
            bank_account_id: reconciliation.bank_account,
            estado: 'aplicado'
          }
        })
        const pagos = response.data.results || response.data || []
        // Filtrar pagos no conciliados
        return pagos.filter(p => !p.is_reconciled)
      } catch (error) {
        console.error('Error al obtener pagos:', error)
        return []
      }
    },
    { enabled: !!reconciliation?.bank_account }
  )

  // Obtener transacciones financieras no conciliadas
  const { data: transaccionesNoConciliadas } = useQuery(
    ['unreconciled-transactions', reconciliation?.bank_account],
    async () => {
      if (!reconciliation?.bank_account) return []
      try {
        const response = await api.get('/financial/financial-transactions/', {
          params: {
            bank_account_id: reconciliation.bank_account,
            is_reconciled: 'false'
          }
        })
        return response.data.results || response.data || []
      } catch (error) {
        console.error('Error al obtener transacciones:', error)
        return []
      }
    },
    { enabled: !!reconciliation?.bank_account }
  )

  const handleConfirmMatch = (match) => {
    if (window.confirm(`¿Confirmar match del movimiento con ${match.tipo === 'pago' ? 'pago' : 'transacción'}?`)) {
      confirmMatchMutation.mutate({
        movimiento_id: match.movimiento_id,
        transaccion_id: match.transaccion_id,
        pago_id: match.pago_id
      })
    }
  }

  const handleDescartar = (movimiento_id) => {
    if (window.confirm('¿Está seguro de que desea descartar este movimiento? No se conciliará.')) {
      descartarMutation.mutate(movimiento_id)
    }
  }

  const handleManualMatch = (movimiento) => {
    setSelectedMovimiento(movimiento)
    setShowManualMatch(true)
  }

  const getConfianzaBadge = (confianza) => {
    const badges = {
      'alta': { bg: 'success', text: 'Alta' },
      'media': { bg: 'warning', text: 'Media' },
      'baja': { bg: 'danger', text: 'Baja' }
    }
    const badge = badges[confianza] || { bg: 'secondary', text: confianza }
    return <Badge bg={badge.bg}>{badge.text}</Badge>
  }

  const getMovimiento = (movimiento_id) => {
    return movimientos?.find(m => m.id === movimiento_id)
  }

  // Filtrar matches por búsqueda
  const filteredMatches = matches.sugeridos?.filter(match => {
    if (!searchTerm) return true
    const movimiento = getMovimiento(match.movimiento_id)
    const searchLower = searchTerm.toLowerCase()
    return (
      movimiento?.descripcion?.toLowerCase().includes(searchLower) ||
      movimiento?.referencia?.toLowerCase().includes(searchLower) ||
      match.razon?.toLowerCase().includes(searchLower)
    )
  }) || []

  const filteredSinMatch = matches.sin_match?.filter(mov => {
    if (!searchTerm) return true
    const searchLower = searchTerm.toLowerCase()
    return (
      mov.descripcion?.toLowerCase().includes(searchLower) ||
      mov.referencia?.toLowerCase().includes(searchLower)
    )
  }) || []

  return (
    <Card className="mt-4">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <div>
          <h5 className="mb-0">Matching de Movimientos</h5>
          <small className="text-muted">
            Revisa y confirma los matches sugeridos automáticamente
          </small>
        </div>
        <Button variant="outline-secondary" size="sm" onClick={onClose}>
          <i className="bi bi-x-lg"></i> Cerrar
        </Button>
      </Card.Header>
      <Card.Body>
        {/* Controles */}
        <Row className="mb-3">
          <Col md={4}>
            <InputGroup>
              <InputGroup.Text>Rango de días:</InputGroup.Text>
              <Form.Control
                type="number"
                min="1"
                max="30"
                value={rangoDias}
                onChange={(e) => setRangoDias(parseInt(e.target.value) || 3)}
              />
              <Button variant="outline-primary" onClick={fetchMatches} disabled={loadingMatches}>
                {loadingMatches ? <Spinner size="sm" /> : 'Actualizar'}
              </Button>
            </InputGroup>
          </Col>
          <Col md={4}>
            <Form.Control
              type="text"
              placeholder="Buscar por descripción o referencia..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Col>
          <Col md={4}>
            <div className="d-flex gap-2 align-items-center">
              <Badge bg="success">{filteredMatches.length} Matches Sugeridos</Badge>
              <Badge bg="warning">{filteredSinMatch.length} Sin Match</Badge>
            </div>
          </Col>
        </Row>

        {/* Matches sugeridos */}
        {loadingMatches ? (
          <div className="text-center py-4">
            <Spinner animation="border" />
            <p className="mt-2">Buscando matches...</p>
          </div>
        ) : (
          <>
            {filteredMatches.length > 0 && (
              <Card className="mb-4">
                <Card.Header>
                  <h6 className="mb-0">Matches Sugeridos ({filteredMatches.length})</h6>
                </Card.Header>
                <Card.Body>
                  <Table responsive striped hover>
                    <thead>
                      <tr>
                        <th>Movimiento</th>
                        <th>Match con</th>
                        <th>Confianza</th>
                        <th>Razón</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredMatches.map((match, idx) => {
                        const movimiento = getMovimiento(match.movimiento_id)
                        return (
                          <tr key={idx}>
                            <td>
                              <div>
                                <strong>{movimiento?.descripcion || 'N/A'}</strong>
                                <br />
                                <small className="text-muted">
                                  {movimiento?.fecha ? formatDateOnlyEsMX(movimiento.fecha, {}, 'N/A') : 'N/A'} - 
                                  {formatCurrency(Math.abs(parseFloat(movimiento?.monto || 0)))}
                                </small>
                              </div>
                            </td>
                            <td>
                              <Badge bg={match.tipo === 'pago' ? 'primary' : 'info'}>
                                {match.tipo === 'pago' ? 'Pago' : 'Transacción'} #{match.pago_id || match.transaccion_id}
                              </Badge>
                            </td>
                            <td>{getConfianzaBadge(match.confianza)}</td>
                            <td>{match.razon || 'N/A'}</td>
                            <td>
                              <div className="d-flex gap-1">
                                <Button
                                  variant="success"
                                  size="sm"
                                  onClick={() => handleConfirmMatch(match)}
                                  disabled={confirmMatchMutation.isLoading}
                                >
                                  <i className="bi bi-check-lg"></i> Confirmar
                                </Button>
                                <Button
                                  variant="outline-info"
                                  size="sm"
                                  onClick={() => handleManualMatch(movimiento)}
                                >
                                  <i className="bi bi-search"></i> Buscar Manual
                                </Button>
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            )}

            {/* Movimientos sin match */}
            {filteredSinMatch.length > 0 && (
              <Card>
                <Card.Header>
                  <h6 className="mb-0">Movimientos Sin Match ({filteredSinMatch.length})</h6>
                </Card.Header>
                <Card.Body>
                  <Table responsive striped hover>
                    <thead>
                      <tr>
                        <th>Fecha</th>
                        <th>Descripción</th>
                        <th>Monto</th>
                        <th>Referencia</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSinMatch.map((mov) => (
                        <tr key={mov.movimiento_id}>
                          <td>{mov.fecha ? formatDateOnlyEsMX(mov.fecha, {}, 'N/A') : 'N/A'}</td>
                          <td>{mov.descripcion || 'N/A'}</td>
                          <td className={mov.monto >= 0 ? 'text-success' : 'text-danger'}>
                            {formatCurrency(Math.abs(parseFloat(mov.monto || 0)))}
                          </td>
                          <td>{mov.referencia || 'N/A'}</td>
                          <td>
                            <div className="d-flex gap-1">
                              <Button
                                variant="outline-info"
                                size="sm"
                                onClick={() => handleManualMatch(getMovimiento(mov.movimiento_id))}
                              >
                                <i className="bi bi-search"></i> Buscar Manual
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleDescartar(mov.movimiento_id)}
                                disabled={descartarMutation.isLoading}
                              >
                                <i className="bi bi-x-lg"></i> Descartar
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            )}

            {filteredMatches.length === 0 && filteredSinMatch.length === 0 && !loadingMatches && (
              <Alert variant="info">
                No hay movimientos pendientes de matching. Todos los movimientos han sido procesados.
              </Alert>
            )}
          </>
        )}
      </Card.Body>

      {/* Modal de búsqueda manual */}
      <Modal show={showManualMatch} onHide={() => { setShowManualMatch(false); setSelectedMovimiento(null) }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Búsqueda Manual de Match</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedMovimiento && (
            <>
              <Alert variant="info">
                <strong>Movimiento a conciliar:</strong><br />
                Fecha: {formatDateOnlyEsMX(selectedMovimiento.fecha, {}, 'N/A')}<br />
                Descripción: {selectedMovimiento.descripcion}<br />
                Monto: {formatCurrency(Math.abs(parseFloat(selectedMovimiento.monto || 0)))}
              </Alert>

              {/* Pagos no conciliados */}
              <h6>Pagos No Conciliados</h6>
              {pagosNoConciliados && pagosNoConciliados.length > 0 ? (
                <Table responsive striped hover size="sm">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Cliente</th>
                      <th>Monto</th>
                      <th>Referencia</th>
                      <th>Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagosNoConciliados.map((pago) => (
                      <tr key={pago.id}>
                        <td>{formatDateOnlyEsMX(pago.payment_date, {}, 'N/A')}</td>
                        <td>{pago.cliente_name || 'N/A'}</td>
                        <td>{formatCurrency(pago.amount || 0)}</td>
                        <td>{pago.reference || 'N/A'}</td>
                        <td>
                          <Button
                            variant="success"
                            size="sm"
                            onClick={() => {
                              handleConfirmMatch({
                                movimiento_id: selectedMovimiento.id,
                                pago_id: pago.id,
                                tipo: 'pago'
                              })
                              setShowManualMatch(false)
                            }}
                          >
                            <i className="bi bi-check-lg"></i> Seleccionar
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <Alert variant="secondary">No hay pagos no conciliados disponibles</Alert>
              )}

              {/* Transacciones financieras no conciliadas */}
              <h6 className="mt-3">Transacciones Financieras No Conciliadas</h6>
              {transaccionesNoConciliadas && transaccionesNoConciliadas.length > 0 ? (
                <Table responsive striped hover size="sm">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Tipo</th>
                      <th>Monto</th>
                      <th>Descripción</th>
                      <th>Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transaccionesNoConciliadas.map((trans) => (
                      <tr key={trans.id}>
                        <td>{formatDateOnlyEsMX(trans.transaction_date, {}, 'N/A')}</td>
                        <td>{trans.transaction_type}</td>
                        <td>{formatCurrency(trans.amount || 0)}</td>
                        <td>{trans.description || 'N/A'}</td>
                        <td>
                          <Button
                            variant="success"
                            size="sm"
                            onClick={() => {
                              handleConfirmMatch({
                                movimiento_id: selectedMovimiento.id,
                                transaccion_id: trans.id,
                                tipo: 'transaccion'
                              })
                              setShowManualMatch(false)
                            }}
                          >
                            <i className="bi bi-check-lg"></i> Seleccionar
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <Alert variant="secondary">No hay transacciones financieras no conciliadas disponibles</Alert>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowManualMatch(false); setSelectedMovimiento(null) }}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  )
}

export default MatchingInterface
